import React, { useState, useEffect, useCallback } from "react";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { fetchProfile } from "../../Services/profile/profileservice";
import { 
  MobileDeviceReportService,
  createActionListService,
  createActionApplyService,
  createActionGetService,
  createActionStatusService,
  createSuperKeyService,
  createSuperRefreshService,
  createSuperDeleteService,
  createKeyService,
  getKeyService,        
  createKeyRefreshService,  
  createKeyRemoveService,   
  createKeyUnregisterService,  
} from "../../Services/MobileDeviceLock/CreateMobileDeviceLock";
import Loader from "../../Loader";
import { FaSync,FaMobileAlt } from "react-icons/fa";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const MobileDeviceLock = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [filters, setFilters] = useState({ status: "", running: "", per_page: 10 });
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [controlList, setControlList] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [apiCredentials, setApiCredentials] = useState({});
  const [superKeyId, setSuperKeyId] = useState("");
  const [keyId, setKeyId] = useState("");
  // const [keyDetails, setKeyDetails] = useState(null);
  const [keyVersion, setKeyVersion] = useState(null);
  const [errors, setErrors] = useState({});
 const [controlActions, setControlActions] = useState([]);
const [controlDetails, setControlDetails] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
  isOpen: false,
  title: "",
  message: "",
  onConfirm: null,
  confirmText: "Confirm",
  cancelText: "Cancel",
  type: "danger" // 'danger', 'warning', 'info'
});


  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
    manufacturer: "",
    model: "",
    imei_no: "",
    imei_no2: "",
    running: true
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchProfile();
      const data = response.data;
      setApiCredentials({
        api_id: data.user_id || "",
        api_key: data.api_key || "",
        token_id: data.token_id || ""
      });
    } catch (error) {
      showErrorToast("Failed to load profile data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedSearch.length === 0 || debouncedSearch.length >= 3) {
      fetchReports();
    }
  }, [debouncedSearch, pagination.current_page, filters]);

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const runningFilter = filters.running === "true" ? true : filters.running === "false" ? false : undefined;
      const payload = {
        page: pagination.current_page,
        per_page: filters.per_page,
        ...(filters.status && { status: filters.status }),
        ...(runningFilter !== undefined && { running: runningFilter }),
        ...(debouncedSearch.length >= 3 && { search: debouncedSearch })
      };

      const response = await MobileDeviceReportService(payload);
      
      if (response?.data) {
        setReportData(response.data || []);
        const paginationData = response.pagination || response.data;
        setPagination({
          total: paginationData.total || 0,
          per_page: paginationData.per_page || 10,
          current_page: paginationData.current_page || 1,
          last_page: paginationData.last_page || 1
        });
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Failed to fetch reports");
    } finally {
      setIsLoading(false);
    }
  };



// Fetch control list when modal opens  
const fetchControlList = useCallback(async (keyId) => {
  if (!keyId) return;
  
  try {
    const payload = { ...apiCredentials, key_id: keyId };
    const response = await createActionListService(payload);
    let items = response.data?.data?.items || response.data?.items || [];
    
    // Get key status to determine if unregister controls should show
    const currentReport = reportData.find(r => r.linked_key?.key_id === keyId);
    const isUnregistered = currentReport?.status?.toLowerCase() === 'unregistered';
    
    // Filter out UNREGISTER controls if key is registered
    if (!isUnregistered) {
      items = items.filter(control => 
        control.name !== 'UNREGISTER_BY_CODE' && 
        control.name !== 'UNREGISTER_BY_SMS'
      );
    }
    
    setControlList(items);
  } catch (error) {
    console.error("❌ Failed to load controls:", error);
    setControlList([]);
  }
}, [apiCredentials, reportData]);


// Fetch control detail to get dynamic actions
const fetchControlDetail = useCallback(async (controlName) => {
  if (!controlName) return;
  
  try {
    const payload = { ...apiCredentials, name: controlName };
    const response = await createActionListService(payload);
    const items = response.data?.data?.items || response.data?.items || [];
    
    if (items.length > 0) {
      const firstItem = items[0];
      setControlDetails(firstItem);
      
      // Get actions from the first ui_config_bottom_sheet item
      const bottomSheetItems = firstItem.ui_config_bottom_sheet?.items || [];
      if (bottomSheetItems.length > 0) {
        const actions = bottomSheetItems[0].actions || [];
        setControlActions(actions);
       //console.log("✅ Control actions loaded:", actions);
      } else {
        setControlActions([]);
      }
    }
  } catch (error) {
    console.error("❌ Failed to load control details:", error);
    setControlActions([]);
    setControlDetails(null);
  }
}, [apiCredentials]);



// Handle control selection and fetch its details
const handleControlSelect = async (control) => {
  setSelectedControl(control);
  setControlActions([]); // Reset actions while loading
  await fetchControlDetail(control.name);
};



  const fetchGetKeyDetails = useCallback(async (keyId) => {
    if (!keyId) return;
    
    try {
      const payload = { ...apiCredentials, key_id: keyId };
      const response = await getKeyService(payload);
      
      // Set Super Key ID from reference_id
      const refId = response.data?.reference_id || response.data?.data?.reference_id;
      if (refId) {
        setSuperKeyId(refId);
       // console.log("Super Key ID set:", refId);
      }
      
      const keyVersions = response.data?.data?.version || response.data?.version || [];
      
      // Ensure keyVersion is always an array
      if (Array.isArray(keyVersions)) {
        setKeyVersion(keyVersions);
      } else if (typeof keyVersions === 'string') {
        setKeyVersion([keyVersions]);
      } else {
        setKeyVersion([]);
      }
    } catch (error) {
      console.error("Failed to get Key Version:", error);
      setKeyVersion([]);
    }
  }, [apiCredentials]);

  const handleOpenModal = async (report) => {
    setSelectedReport(report);
    setSelectedControl(null);
      setControlActions([]); // Reset actions
  setControlDetails(null); // Reset control details
    setActionResult(null);
    setControlList([]);
    setKeyVersion([]);
    setShowModal(true);
    
    //  CRITICAL FIX: Set keyId from report
    const reportKeyId = report?.linked_key?.key_id;
    if (reportKeyId) {
      setKeyId(reportKeyId);
     /// console.log("Key ID set from report:", reportKeyId);
      
      // Fetch both controls and key details
      await Promise.all([
        fetchControlList(reportKeyId),
        fetchGetKeyDetails(reportKeyId)
      ]);
    } else {
      console.warn("No key_id found for this report");
      setKeyId("");
      setSuperKeyId("");
    }
  };

  // Filter controls based on version matching
const getFilteredControls = () => {
 // console.log(" Filtering controls. Total:", controlList.length, "| Versions:", keyVersion);
  
  // If no version filtering needed, return all controls
  if (!keyVersion || !Array.isArray(keyVersion) || keyVersion.length === 0) {
  //  console.log(" No version filter, returning all controls");
    return controlList;
  }
  
  const filtered = controlList.filter(control => {
    // If control has no versions array, INCLUDE it (backwards compatibility)
    if (!control.versions || !Array.isArray(control.versions)) {
     // console.log(`ℹ Control "${control.name}" has no versions, including it`);
      return true;
    }
    
    // Check if ANY keyVersion exists in control.versions
    const hasMatch = keyVersion.some(version => control.versions.includes(version));
   // console.log(`${hasMatch ? '' : ''} Control "${control.name}" versions:`, control.versions, "| Match:", hasMatch);
    return hasMatch;
  });
  
 // console.log(" Filtered controls:", filtered.length);
  return filtered;
};

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleReset = () => { 
    setFilters({ status: "", running: "", per_page: 10 });
    setSearchInput("");
    setPagination({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page && newPage !== pagination.current_page) {
      setPagination(prev => ({ ...prev, current_page: newPage }));
    }
  };





const handleApplyAction = async (actionName) => {
  if (!selectedControl || !keyId) return showErrorToast("No control selected");

  setIsLoading(true);
  try {
    const payload = {
      ...apiCredentials,
      key_id: keyId,
      control_name: selectedControl.name,
      action_name: actionName
    };

    console.log("payload for apply action",payload);

    const response = await createActionApplyService(payload);

    const isSuccess =
      response?.status?.code === 200 ||
      response?.status === 200 ||
      response?.success === true;

    if (isSuccess) {
      // Get toast message from action definition
      const actionItem = controlActions.find(a => a.action === actionName);
      const toastMessage = actionItem?.toast || 
        response?.message || 
        response?.data?.message || 
        `${actionName} applied successfully`;

      setActionResult({
        type: "control",
        status: "success",
        message: toastMessage,
        data: response.data
      });

      showSuccessToast(toastMessage);
    } else {
      const serverMessage =
        response?.data?.error?.message ||
        response?.error?.message ||
        response?.message ||
        "Action failed";

      setActionResult({
        type: "control",
        status: "error",
        message: serverMessage,
        data: response.data
      });

      showErrorToast(serverMessage);
    }

  } catch (error) {
    const serverMessage =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      "Action failed";

    setActionResult({
      type: "control",
      status: "error",
      message: serverMessage,
      data: error.response?.data
    });

    showErrorToast(serverMessage);
  } finally {
    setIsLoading(false);
  }
};





const handleGetAction = async () => {
  if (!keyId) return showErrorToast("No key ID available");

  setIsLoading(true);
  try {
    const payload = { ...apiCredentials, key_id: keyId };
    const response = await createActionGetService(payload);

    const isSuccess =
      response?.status?.code === 200 ||   // current format
      response?.status === 200 ||         // legacy format
      response?.success === true;         // wrapped format

    if (isSuccess) {
      const successMsg =
        response?.message ||
        response?.data?.message ||
        "Action details retrieved";

      setActionResult({
        type: "info",
        status: "success",
        message: successMsg,
        data: response.data
      });

      showSuccessToast(successMsg);
    } else {
      const serverMessage =
        response?.data?.error?.message ||
        response?.error?.message ||
        response?.message ||
        "Failed to get actions";

      setActionResult({
        type: "info",
        status: "error",
        message: serverMessage,
        data: response.data
      });

      showErrorToast(serverMessage);
    }

  } catch (error) {
    const serverMessage =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      "Failed to get actions";

    setActionResult({
      type: "info",
      status: "error",
      message: serverMessage,
      data: error.response?.data
    });

    showErrorToast(serverMessage);
  } finally {
    setIsLoading(false);
  }
};



const handleGetStatus = async () => {
  if (!keyId) return showErrorToast("No key ID available");

  setIsLoading(true);
  try {
    const payload = { ...apiCredentials, key_id: keyId };
    const response = await createActionStatusService(payload);

   // console.log("raw status response", response);

    //  universal success detection
    const isSuccess =
      response?.status?.code === 200 ||   // new format
      response?.status === 200 ||         // wrapped format
      response?.success === true;         // older format

    if (isSuccess) {
      const successMsg =
        response?.message ||
        response?.data?.message ||
        "Status retrieved";

      setActionResult({
        type: "info",
        status: "success",
        message: successMsg,
        data: response.data
      });

      showSuccessToast(successMsg);
    } else {
      const serverMessage =
        response?.data?.error?.message ||
        response?.error?.message ||
        response?.message ||
        "Failed to get status";

      setActionResult({
        type: "info",
        status: "error",
        message: serverMessage,
        data: response.data
      });

      showErrorToast(serverMessage);
    }

  } catch (error) {
    const serverMessage =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      "Failed to get status";

    setActionResult({
      type: "info",
      status: "error",
      message: serverMessage,
      data: error.response?.data
    });

    showErrorToast(serverMessage);
  } finally {
    setIsLoading(false);
  }
};


  const statusColors = {
    CREATED: "bg-blue-100 text-blue-800",
    PENDING: "bg-yellow-100 text-yellow-800",
    ACTIVE: "bg-green-100 text-green-800",
    FINISHED: "bg-indigo-100 text-indigo-800",
    FAILED: "bg-red-100 text-red-800",
    INITIATED: "bg-gray-100 text-gray-800"
  };

  const DetailRow = ({ label, value }) => (
    <div className="py-0.5 text-sm"><span className="font-semibold">{label}:</span> {value}</div>
  );

  const buildPayload = useCallback((additionalData = {}) => ({
    ...apiCredentials,
    ...additionalData
  }), [apiCredentials]);



const validateForm = () => {
  const newErrors = {};

  if (!formData.name?.trim()) newErrors.name = "Name required";

  if (!formData.mobile_number?.trim()) {
    newErrors.mobile_number = "Mobile required";
  } else if (!/^[6-9]\d{9}$/.test(formData.mobile_number)) {
    newErrors.mobile_number = "Invalid mobile number";
  }

  if (!formData.manufacturer?.trim())
    newErrors.manufacturer = "Manufacturer required";

  if (!formData.model?.trim())
    newErrors.model = "Model required";

  // IMEI 1 (mandatory)
  if (!formData.imei_no?.trim()) {
    newErrors.imei_no = "IMEI required";
  } else if (!/^\d{15}$/.test(formData.imei_no)) {
    newErrors.imei_no = "IMEI must be 15 digits";
  }

  // IMEI 2 (mandatory)
  if (!formData.imei_no2?.trim()) {
    newErrors.imei_no2 = "IMEI No 2 required";
  } else if (!/^\d{15}$/.test(formData.imei_no2)) {
    newErrors.imei_no2 = "IMEI No 2 must be 15 digits";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(prev => { const newErrors = { ...prev }; delete newErrors[name]; return newErrors; });
  };

  const handleApiError = (error, context = "Operation") => {
    if (!error.response) {
      showErrorToast("Network error");
      return null;
    }

    const { status, data } = error.response;
    if (status === 403) {
      showErrorToast(data?.message || "Invalid API key");
    } else if (status === 422) {
      showErrorToast(data?.message || "Validation error");
    } else if (status === 500) {
      if (data?.error?.errorCode === "UNABLE_TO_CREATE_KEY") {
        return "DEVICE_ALREADY_REGISTERED";
      }
      showErrorToast(data?.error?.message || data?.message || "Server error");
    } else {
      showErrorToast(data?.message || `${context} failed`);
    }
    return null;
  };

  const createInternalKey = async (deviceInfo, referenceId = null) => {
    try {
      const keyPayload = buildPayload({
        device_info: deviceInfo,
        ...(referenceId && { reference_id: referenceId, reference_type: "SUPER_KEY" })
      });

      const keyResponse = await createKeyService(keyPayload);
      
      if (keyResponse.status >= 400) {
        console.error("Internal key creation failed:", keyResponse.data?.message);
        return null;
      }

      const keyData = keyResponse.data?.data || keyResponse.data;
      const createdKeyId = keyData?.id || keyData?.key_id;
      setKeyId(createdKeyId);
      // setKeyDetails(keyData);
      console.log("Internal key created:", createdKeyId);
      return keyData;
    } catch (error) {
      console.error("Internal key creation error:", error);
      return null;
    }
  };

  //  FIXED: Device Key Refresh
const refreshKeyInternal = async () => {
  if (!keyId) return showErrorToast("No Key ID available");

  setIsLoading(true);
  try {
    const payload = buildPayload({ key_id: keyId });
    const response = await createKeyRefreshService(payload);

    const isSuccess =
      response?.status?.code === 200 || response?.code === 200;

    if (isSuccess) {
      setActionResult({
        type: "deviceKey",
        status: "success",
        message: "Device Key refreshed successfully",
        data: response.data
      });

      showSuccessToast(
        response?.message || "Device Key refreshed"
      );
    } else {
      // API returned error structure but request succeeded technically
      const serverMessage =
        response?.error?.message ||
        response?.message ||
        "Device Key refresh failed";

      setActionResult({
        type: "deviceKey",
        status: "error",
        message: serverMessage,
        data: response
      });

      showErrorToast(serverMessage);
    }

  } catch (error) {
    const serverMessage =
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      "Device Key refresh failed";

    setActionResult({
      type: "deviceKey",
      status: "error",
      message: serverMessage,
      data: error.response?.data
    });

    showErrorToast(serverMessage);
  } finally {
    setIsLoading(false);
  }
};


//  FIXED: Device Key Remove
const removeKeyInternal = () => {
  if (!keyId) return showErrorToast("No Key ID available");

  setConfirmModal({
    isOpen: true,
    title: "Remove Device Key",
    message: `Are you sure you want to remove this device key?\n\nKey ID: ${keyId.substring(0, 20)}...\n\nThis will disconnect the device from the system.`,
    confirmText: "Yes, Remove",
    cancelText: "Cancel",
    type: "warning",
    onConfirm: async () => {
      setConfirmModal({ ...confirmModal, isOpen: false });
      setIsLoading(true);
      
      try {
        const payload = buildPayload({ key_id: keyId });
        const response = await createKeyRemoveService(payload);
        console.log("remove response:", response);

        // Handle error-style response even if promise resolved
        if (response?.success === false || response?.status === 500 || response?.data?.status?.type === "error") {
          const errorMessage =
            response?.data?.error?.message ||
            response?.data?.message ||
            response?.data?.status?.message ||
            "Device Key removal failed";

          setActionResult({
            type: "deviceKey",
            status: "error",
            message: `❌ ${errorMessage}`,
            data: response.data
          });

          showErrorToast(errorMessage);
          return;
        }

        // Only reach here on real success
        setActionResult({
          type: "deviceKey",
          status: "success",
          message: " Device Key removed successfully",
          data: response.data
        });

        showSuccessToast(
          response.data?.message || 
          response.data?.status?.message || 
          "🗑️ Device Key removed"
        );
      } catch (error) {
        console.error("remove error:", error);

        const serverMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Device Key removal failed";

        setActionResult({
          type: "deviceKey",
          status: "error",
          message: `❌ ${serverMessage}`,
          data: error?.response?.data || error
        });

        showErrorToast(serverMessage);
      } finally {
        setIsLoading(false);
      }
    }
  });
};




const handleCreateSuperKey = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsLoading(true);
  try {
    const deviceInfo = {
      manufacturer: formData.manufacturer,
      model: formData.model,
      imei_no: formData.imei_no,
      imei_no2: formData.imei_no2,
      running: formData.running
    };

    const payload = buildPayload({
      name: formData.name,
      mobile_number: parseInt(formData.mobile_number),  
      device_info: deviceInfo
    });

    const response = await createSuperKeyService(payload);

    console.log("res Super Key", response);  
    
    // Check if response indicates an error (status 500 in data)
    if (response.data?.status === 500 || response.data?.error?.errorCode) {
      const errorMessage = response.data?.error?.message 
        || response.data?.message 
        || "Unable to create Super Key";
      
      showErrorToast(errorMessage);
      return;
    }
    
    // Extract success message from service response
    const successMessage = response.data?.message 
      || response.data?.data?.message 
      || "Super Key created successfully!";
    
    const superKeyData = response.data?.data || response.data;
    const createdSuperKeyId = superKeyData?.id || superKeyData?.key_id;
    
    setSuperKeyId(createdSuperKeyId);
    showSuccessToast(successMessage);
    fetchReports(); // Refresh the table

  } catch (error) {
    console.error("Super Key Creation Error:", error);
    
    // Extract error message with proper priority order
    const errorMessage = error.response?.data?.error?.message 
      || error.response?.data?.message 
      || error.response?.data?.status?.message
      || error.message 
      || "Failed to create Super Key. Please try again.";
    
    showErrorToast(errorMessage);
    
  } finally {
    setIsLoading(false);
  }
};




//  FIXED: Super Key Delete with unregister error handling
// const handleDeleteSuperKey = () => {
//   if (!superKeyId) return showErrorToast("No Super Key to delete");

//   setConfirmModal({
//     isOpen: true,
//     title: "Delete Super Key",
//     message: `⚠️ WARNING: This will delete the Super Key${keyId ? ' and unregister the Device Key' : ''}.\n\nSuper Key ID: ${superKeyId.substring(0, 20)}...\n\nThis action cannot be undone. Are you sure?`,
//     confirmText: "Yes, Delete",
//     cancelText: "Cancel",
//     type: "danger",
//     onConfirm: async () => {
//       setConfirmModal({ ...confirmModal, isOpen: false });
//       setIsLoading(true);
      
//       try {
//         let stepMessage = "";
        
//         // Step 1: Unregister Device Key (only if keyId exists)
//         if (keyId) {
//           try {
//             console.log("Step 1: Attempting to unregister Device Key:", keyId);
//             const unregisterPayload = buildPayload({ key_id: keyId });
//             const unregisterResponse = await createKeyUnregisterService(unregisterPayload);
            
//             // Handle error-style response even if promise resolved
//             if (
//               unregisterResponse?.success === false || 
//               unregisterResponse?.status === 500 || 
//               unregisterResponse?.data?.status?.type === "error"
//             ) {
//               const errorMessage =
//                 unregisterResponse?.data?.error?.message ||
//                 unregisterResponse?.data?.message ||
//                 unregisterResponse?.data?.status?.message ||
//                 "Device Key unregistration failed";
              
//               throw new Error(errorMessage);
//             }
            
//             // Success case
//             showInfoToast("✓ Step 1/2: Device Key unregistered");
//             stepMessage = "Device Key unregistered and ";
//             await new Promise(resolve => setTimeout(resolve, 1500));
            
//           } catch (unregError) {
//             console.error("Unregister error:", unregError);
            
//             // Extract error details with same priority as unregisterKeyInternal
//             const errorCode = unregError.response?.data?.error?.errorCode;
//             const errorMessage = 
//               unregError?.response?.data?.error?.message ||
//               unregError?.response?.data?.message ||
//               unregError?.message ||
//               "Device Key unregistration failed";
            
//             // Check if key is already unregistered (allow continuation)
//             if (
//               errorCode === "INVALID_KEY_STATE_TRANSITION" && 
//               errorMessage.includes("UNREGISTERED")
//             ) {
//               console.log("Device Key already unregistered, proceeding to delete Super Key");
//               showInfoToast("✓ Device Key already unregistered");
//               stepMessage = "Device Key already unregistered and ";
//               await new Promise(resolve => setTimeout(resolve, 1000));
//             } else {
//               // For any other unregister error, stop and show detailed error
//               setActionResult({
//                 type: "deviceKey",
//                 status: "error",
//                 message: `❌ ${errorMessage}`,
//                 data: unregError?.response?.data || unregError
//               });
              
//               showErrorToast(errorMessage);
//               setIsLoading(false);
//               return; // Exit early - don't proceed to Super Key deletion
//             }
//           }
//         }
        
//         // Step 2: Delete Super Key (only reached if Step 1 succeeded or was skipped)
//         console.log("Step 2: Deleting Super Key:", superKeyId);
//         console.log("API Credentials:", apiCredentials);
        
//         const payload = { ...apiCredentials, super_key_id: superKeyId };
//         const response = await createSuperDeleteService(payload);
        
//         // Extract success message from response
//         const successMessage = 
//           response.data?.message || 
//           response.data?.data?.message || 
//           `${stepMessage}Super Key deleted successfully`;
        
//         setActionResult({
//           type: "superKey",
//           status: "success",
//           message: `✅ ${successMessage}`,
//           data: response.data
//         });
        
//         showSuccessToast(successMessage);
        
//         // Clear states
//         setSuperKeyId("");
//         setKeyId("");
//         setKeyDetails(null);
        
//         // Close modal and refresh
//         setShowModal(false);
//         fetchReports();
        
//       } catch (error) {
//         console.error("Super Key Deletion Error:", error);
        
//         // Extract error message with proper priority
//         const serverMessage =
//           error.response?.data?.error?.message ||
//           error.response?.data?.errors?.api_id?.[0] ||
//           error.response?.data?.errors?.api_key?.[0] ||
//           error.response?.data?.message ||
//           error.response?.data?.status?.message ||
//           "Super Key deletion failed";

//         setActionResult({
//           type: "superKey",
//           status: "error",
//           message: `❌ ${serverMessage}`,
//           data: error.response?.data
//         });

//         showErrorToast(serverMessage);
//       } finally {
//         setIsLoading(false);
//       }
//     }
//   });
// };


//  FIXED: Super Key Delete (without unregister step)
const handleDeleteSuperKey = () => {
  if (!superKeyId) return showErrorToast("No Super Key to delete");

  setConfirmModal({
    isOpen: true,
    title: "Delete Super Key",
    message: `⚠️ WARNING: This will permanently delete the Super Key.\n\nSuper Key ID: ${superKeyId.substring(0, 20)}...\n\nThis action cannot be undone. Are you sure?`,
    confirmText: "Yes, Delete",
    cancelText: "Cancel",
    type: "danger",
    onConfirm: async () => {
      setConfirmModal({ ...confirmModal, isOpen: false });
      setIsLoading(true);
      
      try {
       // console.log("Deleting Super Key:", superKeyId);
      //  console.log("API Credentials:", apiCredentials);
        
        const payload = { ...apiCredentials, super_key_id: superKeyId };
        const response = await createSuperDeleteService(payload);
        
        // Check for error-style response even if promise resolved
        if (
          response?.success === false || 
          response?.status === 500 || 
          response?.data?.status?.type === "error"
        ) {
          const errorMessage =
            response?.data?.error?.message ||
            response?.data?.message ||
            response?.data?.status?.message ||
            "Super Key deletion failed";
          
          throw new Error(errorMessage);
        }
        
        // Extract success message from response
        const successMessage = 
          response.data?.message || 
          response.data?.data?.message || 
          response.data?.status?.message ||
          "Super Key deleted successfully";
        
        setActionResult({
          type: "superKey",
          status: "success",
          message: `✅ ${successMessage}`,
          data: response.data
        });
        
        showSuccessToast(successMessage);
        
        // Clear states
        setSuperKeyId("");
        setKeyId("");
        // setKeyDetails(null);
        
        // Close modal and refresh
        setShowModal(false);
        fetchReports();
        
      } catch (error) {
        console.error("Super Key Deletion Error:", error);
        
        // Extract error message with proper priority
        const serverMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.errors?.api_id?.[0] ||
          error.response?.data?.errors?.api_key?.[0] ||
          error.response?.data?.message ||
          error.response?.data?.status?.message ||
          error.message ||
          "Super Key deletion failed";

        setActionResult({
          type: "superKey",
          status: "error",
          message: `❌ ${serverMessage}`,
          data: error.response?.data || error
        });

        showErrorToast(serverMessage);
      } finally {
        setIsLoading(false);
      }
    }
  });
};




//  FIXED: Device Key Unregister
const unregisterKeyInternal = () => {
  if (!keyId) return showErrorToast("No Key ID available");

  setConfirmModal({
    isOpen: true,
    title: "Unregister Device Key",
    message: `Are you sure you want to unregister this device key?\n\nKey ID: ${keyId.substring(0, 20)}...\n\nThis action cannot be undone and will remove the device from your system.`,
    confirmText: "Yes, Unregister",
    cancelText: "Cancel",
    type: "danger",
    onConfirm: async () => {
      setConfirmModal({ ...confirmModal, isOpen: false });
      setIsLoading(true);

      try {
        const payload = buildPayload({ key_id: keyId });
        const response = await createKeyUnregisterService(payload);
      //  console.log("unregister ", response);

        // Handle error-style response even if promise resolved
        if (response?.success === false || response?.status === 500 || response?.data?.status?.type === "error") {
          const errorMessage =
            response?.data?.error?.message ||          
            response?.data?.message ||
            response?.data?.status?.message ||
            "Device Key unregistration failed";

          setActionResult({
            type: "deviceKey",
            status: "error",
            message: `❌ ${errorMessage}`,
            data: response.data
          });

          showErrorToast(errorMessage);
          return;
        }

        // Only reach here on real success
        setActionResult({
          type: "deviceKey",
          status: "success",
          message: "✅ Device Key unregistered successfully",
          data: response.data
        });

        showSuccessToast(
          response.data?.message ||
          response.data?.status?.message ||
          "🔓 Device Key unregistered"
        );
      } catch (error) {
        console.error("unregister error:", error);

        const serverMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Device Key unregistration failed";

        setActionResult({
          type: "deviceKey",
          status: "error",
          message: `❌ ${serverMessage}`,
          data: error?.response?.data || error
        });

        showErrorToast(serverMessage);
      } finally {
        setIsLoading(false);
      }
    }
  });
};




  //  FIXED: Super Key Refresh
  const handleRefreshSuperKey = async () => {
    if (!superKeyId) return showErrorToast("No Super Key to refresh");
    setIsLoading(true);
    try {
      const payload = buildPayload({ super_key_id: superKeyId });
      const response = await createSuperRefreshService(payload);
      
      setActionResult({
        type: "superKey",
        status: "success",
        message: " Super Key refreshed successfully",
        data: response.data
      });
      showSuccessToast(" Super Key refreshed successfully");
    } catch (error) {
      setActionResult({
        type: "superKey",
        status: "error",
        message: " Super Key refresh failed",
        data: error.response?.data
      });
      handleApiError(error, "Super Key Refresh");
    } finally {
      setIsLoading(false);
    }
  };




const qrMeta =
  selectedReport?.linked_key?.response_data?.gateway_meta?.qr_code_data;

let qrString = "";

if (qrMeta) {
  if (typeof qrMeta === "string") {
    qrString = qrMeta;
  } else if (typeof qrMeta === "object") {
    qrString =
      qrMeta["android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION"] ??
      JSON.stringify(qrMeta);
  }
}

const qrImageUrl = qrString
  ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrString)}`
  : null;




return ( 
  <>
    <Seo />
    {isLoading && <Loader />}
    <Header heading="Mobile Device Key and Reports" />

    <div className="bg-gray-50 md:px-1 md:py-1 px-0 py-0 rounded-lg shadow mb-2">



     
        
        {/* Device Information Form */}
        <div className="bg-white rounded-lg shadow md:px-2 md:py-2 px-1 py-0">
          <h2 className="text-xl font-bold mb-2">📱 Device Information</h2>
          <form onSubmit={handleCreateSuperKey}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2 mb-2">
              {[
                { name: "name", label: "Customer Name", type: "text", placeholder: "Enter name" },
                { name: "mobile_number", label: "Mobile", type: "tel", placeholder: "10-digit mobile", maxLength: 10 },
                { name: "manufacturer", label: "Manufacturer", type: "text", placeholder: "e.g., Samsung" },
                { name: "model", label: "Model", type: "text", placeholder: "e.g., Galaxy S21" },
                { name: "imei_no", label: "IMEI No", type: "text", placeholder: "15-digit IMEI", maxLength: 15 },
                { name: "imei_no2", label: "IMEI No 2", type: "text", placeholder: "15-digit IMEI", maxLength: 15 }
              ].map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-1">{field.label}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    maxLength={field.maxLength}
                    placeholder={field.placeholder}
                    className={`w-full px-2 py-1 border rounded focus:ring-2 ${errors[field.name] ? "border-red-500" : "border-gray-300"}`}
                  />
                  {errors[field.name] && <p className="text-red-600 text-xs mt-1">{errors[field.name]}</p>}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" name="running" checked={formData.running} onChange={handleChange} className="mr-2" />
                <span className="text-sm">Device Running</span>
              </label>
              <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                🔐 Create Lock
              </button>
            </div>
          </form>
        </div>
   
    </div>



<div className="flex justify-center">
<div className="space-y-2">
  <h2 className="text-lg font-semibold">
    <a
      href="/GetIMEINumber.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      Click here to know how to get IMEI Number
    </a>
  </h2>

  <h2 className="text-lg font-semibold">
    <a
      href="/SIMToolkitAPP.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      Click here to know how to use SIM Toolkit App
    </a>
  </h2>
</div>
</div>





<div className="max-w-4xl mx-auto p-2 sm:p-3">

  <h2 className="text-lg sm:text-2xl font-bold text-center text-gray-800 mb-1">
    📱 Mobile Device Management – User Instructions
  </h2>

  <p className="text-center text-gray-500 mb-4 text-xs sm:text-sm">
    Follow the below steps to create a device Lock.
  </p>

  <div className="bg-white border rounded-lg shadow-sm p-3 sm:p-5 space-y-3 text-xs sm:text-sm">

    {/* Row 1 */}
    <div className="grid grid-cols-12 gap-2">
      <div className="col-span-12 sm:col-span-3 font-semibold text-gray-800">
        1) Create Lock
      </div>
      <div className="col-span-12 sm:col-span-9 text-gray-600">
      Fill the customer Name, Mobile, Model no, Manufacturer, IMEI 1, IMEI 2 to create Device Lock
      </div>
    </div>


    {/* Row 3 */}
    <div className="grid grid-cols-12 gap-2">
      <div className="col-span-12 sm:col-span-3 font-semibold text-gray-800">
        2) QR & App Setup
      </div>
      <div className="col-span-12 sm:col-span-9 text-gray-600">
        Scan QR Code and Install the APK file in Lock Created Device/Mobile, Open SimToolKit App in Mobile Enter the IMEI 1,IMEI 2 then Click on Grant Permission, Enable All Permission then click on Register.
      </div>
    </div>

    {/* Row 5 */}
    <div className="grid grid-cols-12 gap-2">
      <div className="col-span-12 sm:col-span-3 font-semibold text-gray-800">
        3) Common Controls
      </div>
      <div className="col-span-12 sm:col-span-9 text-gray-600">
        Lock / Unlock device, ring device, Send Audio Payment remainder, Get Device Location, Unlock Device.
      </div>
    </div>

    {/* Warning */}
    <div className="bg-yellow-50 border border-yellow-300 rounded-md p-2 text-yellow-800 text-xs sm:text-sm mt-3">
      ⚠️ <b>Important:</b> Super Key can be deleted only if the device key is already unregistered.
    </div>

  </div>

</div>

    <h2 className="text-center text-black font-semibold text-2xl mt-1"> Device Reports</h2>
    <div className="container px-2 mt-1">
      {/* Filters */}
      <div className="bg-white rounded shadow p-1 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="md:col-span-2 relative">
            <input
              type="text"
              placeholder="Search (min3 chars)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            {searchInput.length > 0 && searchInput.length < 3 && (
              <span className="absolute right-2 top-2 text-xs text-gray-400">{3 - searchInput.length} more</span>
            )}
          </div>
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange("status", e.target.value)} 
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {["PENDING", "ACTIVE", "FINISHED", "FAILED", "CREATED"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filters.running}
            onChange={(e) => handleFilterChange("running", e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="true">Running</option>
            <option value="false">Stopped</option>
          </select>
          <select 
            value={filters.per_page} 
            onChange={(e) => handleFilterChange("per_page", Number(e.target.value))} 
            className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button 
            onClick={handleReset} 
            className="bg-gray-600 text-white px-3 py-1.5 text-sm rounded hover:bg-gray-700 flex items-center justify-center gap-1"
          >
            <FaSync size={12} /> Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-700 text-white">
              <tr>
                {["ID", "Name", "Mobile", "Device", "IMEI","SuperID/KeyID", "Status", "Run", "Created", "Actions"].map(h => (
                  <th key={h} className="px-2 py-2 text-left text-xs font-medium text-white uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.length > 0 ? reportData.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 text-xs">{r.id}</td>
                  <td className="px-2 py-2 text-xs">{r.name || "N/A"}</td>
                  <td className="px-2 py-2 text-xs">{r.mobile_number || "N/A"}</td>
                  <td className="px-2 py-2 text-xs">{r.manufacturer || ""} {r.model || ""}</td>
                  <td className="px-2 py-2 text-xs leading-tight">{r.imei_no}<br/>{r.imei_no2 || ""}</td>
                  <td className="px-2 py-2 text-xs leading-tight">{r.super_key_id || ""}<br/>{r.key_id || ""} </td>
                 <td className="px-2 py-2"> <span className={`px-1.5 py-0.5 text-xs rounded-full ${statusColors[r.status] || "bg-gray-100 text-gray-800"}`}>  {r.status} </span></td>
                  <td className="px-2 py-2">
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${r.running ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                      {r.running ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      {r.linked_key?.id && (
                        <button 
                          onClick={() => handleOpenModal(r)} 
                          className="text-green-600 hover:text-green-900 p-1 ml-1"
                          title="Quick Controls"
                        >
                          <FaMobileAlt size={24} />
                        </button>
                      )}
                    </div>


                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="px-2 py-6 text-center text-sm text-gray-500">
                    {isLoading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {reportData.length > 0 && pagination.total > 0 && (
          <div className="px-3 py-2 flex items-center justify-between border-t border-gray-200 text-xs">
            <span className="text-gray-700">
              {(pagination.current_page - 1) * pagination.per_page + 1}-
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
            </span>
            <div className="flex gap-1">
              <button 
                onClick={() => handlePageChange(pagination.current_page - 1)} 
                disabled={pagination.current_page === 1} 
                className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-xs"
              >
                Prev
              </button>
              {[...Array(pagination.last_page)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === pagination.last_page || (page >= pagination.current_page - 1 && page <= pagination.current_page + 1)) {
                  return (
                    <button 
                      key={page} 
                      onClick={() => handlePageChange(page)} 
                      className={`px-2 py-1 border rounded text-xs ${pagination.current_page === page ? "bg-blue-600 text-white" : "hover:bg-gray-50"}`}
                    >
                      {page}
                    </button>
                  );
                }
                if (page === pagination.current_page - 2 || page === pagination.current_page + 2) {
                  return <span key={page} className="px-1 text-xs">...</span>;
                }
                return null;
              })}
              <button 
                onClick={() => handlePageChange(pagination.current_page + 1)} 
                disabled={pagination.current_page === pagination.last_page} 
                className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-xs"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

  {/*  ENHANCED MODAL WITH FIXED KEY MANAGEMENT */}
{showModal && selectedReport && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1" onClick={() => setShowModal(false)}>
    <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 py-1 flex justify-between items-center rounded-t-lg z-10">
     <div className="flex items-center gap-3 flex-wrap">
  <h4 className="text-xl font-bold whitespace-nowrap">
    📱 {selectedReport.name}
  </h4>

  <p className="text-sm opacity-90 whitespace-nowrap">
    ID: {selectedReport.id}
  </p>

  <span className="text-sm opacity-90 whitespace-nowrap">
    | Mobile: {selectedReport.mobile_number}
  </span>

  {keyId && (
    <span className="text-sm opacity-90 whitespace-nowrap">
      | Key: {keyId}
    </span>
  )}
</div>

        <button onClick={() => setShowModal(false)} className="text-white hover:text-red-200 text-3xl font-bold leading-none">×</button>
      </div>

      <div className="p-1 space-y-1">
        {/* Basic & Device Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
          <div className="bg-gray-50 p-1 rounded-lg">
            <h4 className="font-bold text-sm mb-2 border-b pb-1">📋 Basic Information</h4>
            <div className="space-y-1 text-xs">
              <DetailRow label="Mobile" value={selectedReport.mobile_number} />
              <DetailRow label="Status" value={<span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColors[selectedReport?.linked_key?.status]}`}>{selectedReport?.linked_key?.status}</span>} />
              <DetailRow label="Running" value={selectedReport.running ? "✅ Yes" : "❌ No"} />
                   {selectedReport?.linked_key?.response_data?.journey?.length > 0 ? (
                  <ul className="space-y-1 text-xs max-h-24 overflow-y-auto">
                    {selectedReport.linked_key.response_data.journey.map((item, index) => (
                      <li key={index} className="flex justify-between border rounded px-2 py-1 bg-white">
                        <span className="font-semibold">{item.state}</span>
                        <span className="text-gray-500 text-[10px]">{new Date(item.created_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 text-center py-4"></p>
                )}
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-bold text-sm mb-2 border-b pb-1">📱 Device Info</h4>
            <div className="space-y-1 text-xs">
              <DetailRow label="Manufacturer" value={selectedReport.manufacturer} />
              <DetailRow label="Model" value={selectedReport.model} />
              <DetailRow label="IMEI" value={selectedReport.imei_no} />
              {selectedReport.imei_no2 && <DetailRow label="IMEI 2" value={selectedReport.imei_no2} />}
            </div>
          </div>

          <div className="bg-gray-50 p-2 rounded-lg">
            {selectedReport?.linked_key?.response_data?.gateway_meta?.qr_code_data ? (
              <div>
                <p className="font-bold text-sm mb-2 border-b pb-1">📲 APK Download</p>
               {qrImageUrl && (
  <img
    src={qrImageUrl}
    alt="APK QR Code"
    className="border-2 border-blue-500 h-24 w-24 rounded-lg mx-auto block shadow-md"
  />
)}
                <p className="text-center text-xs text-gray-600 mt-1">Scan to download APK</p>
              </div>
            ) : (
              <div>
                <p className="font-bold text-sm mb-2 border-b pb-1">🧭 Journey Status</p>
                {selectedReport?.linked_key?.response_data?.journey?.some((j) => j.state === "REGISTERED") && (
                  <div className="mb-2 p-2 text-xs bg-green-100 text-green-700 rounded-lg border border-green-300">
                     APK downloaded and registered successfully
                  </div>
                )}
                {selectedReport?.linked_key?.response_data?.journey?.length > 0 ? (
                  <ul className="space-y-1 text-xs max-h-24 overflow-y-auto">
                    {selectedReport.linked_key.response_data.journey.map((item, index) => (
                      <li key={index} className="flex justify-between border rounded px-2 py-1 bg-white">
                        <span className="font-semibold">{item.state}</span>
                        <span className="text-gray-500 text-[10px]">{new Date(item.created_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 text-center py-4">No journey data available</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ========== KEY MANAGEMENT SECTION (SHOWS WHEN keyId EXISTS) ========== */}
        {keyId && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 md:p-2 p-1 rounded-xl border-2 border-blue-200">
            <h3 className="text-lg font-bold mb-1 text-blue-900 flex items-center gap-2">
              🔐 Key Management Center
              {!superKeyId && <span className="text-xs text-orange-600 font-normal">(Loading Super Key...)</span>}
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              
              {/* LEFT: Super Key Management - SHOWS WHEN superKeyId IS SET */}
              {superKeyId ? (
                <div className="bg-white rounded-xl shadow-lg p-2 border border-blue-300">
                  <h4 className="text-md font-bold mb-2 flex items-center gap-2 text-blue-800">
                    🔑 Super Key
                  </h4>
                  
                  <div className="bg-blue-50 p-1 rounded-lg mb-2 break-all border-2 border-blue-300">
                    <p className="text-xs font-mono text-blue-900">{superKeyId}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button 
                      onClick={handleRefreshSuperKey} 
                      disabled={isLoading}
                      className="bg-blue-600 text-white py-2.5 px-3 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <FaSync className={isLoading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button 
                      onClick={handleDeleteSuperKey} 
                      disabled={isLoading}
                      className="bg-red-600 text-white py-2.5 px-3 rounded-lg hover:bg-red-700 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      🗑️ Delete
                    </button>
                  </div>

      {actionResult?.type === "superKey" && (
  <div className={`p-3 rounded-lg text-xs border-2 ${
    actionResult.status === "success" 
      ? "bg-green-50 border-green-300" 
      : "bg-red-50 border-red-300"
  }`}>
    <p className={`font-bold mb-2 ${
      actionResult.status === "success" ? "text-green-800" : "text-red-800"
    }`}>
      {actionResult.message}
    </p>
    {actionResult.data && (
      <details className="mt-2">
        <summary className="cursor-pointer text-blue-700 font-semibold hover:text-blue-900">
          📄 View Response Details
        </summary>
        <div className="mt-2 bg-white rounded-lg border-2 border-gray-200 overflow-hidden max-h-80 overflow-y-auto">
          <table className="min-w-full text-[11px]">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-gray-700 border-b">Field</th>
                <th className="px-3 py-2 text-left font-bold text-gray-700 border-b">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(actionResult.data).map(([key, value]) => {
                // Use the same formatting logic as Device Key above
                let displayValue;
                
                if (value === null || value === undefined) {
                  displayValue = <span className="text-gray-400 italic">null</span>;
                } else if (typeof value === 'boolean') {
                  displayValue = (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {value ? '✓ True' : '✗ False'}
                    </span>
                  );
                } else if (typeof value === 'object' && !Array.isArray(value)) {
                  if (key === 'collection_info' || key === 'meta') {
                    displayValue = (
                      <div className="bg-gray-50 rounded px-2 py-1 space-y-0.5">
                        {Object.entries(value).map(([subKey, subValue]) => (
                          <div key={subKey} className="text-[10px]">
                            <span className="font-semibold">{subKey}:</span> {JSON.stringify(subValue)}
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    displayValue = (
                      <details className="inline">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-[10px]">
                          Object ▼
                        </summary>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-[9px] overflow-auto max-h-24 border">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </details>
                    );
                  }
                } else if (key.includes('created_at') || key.includes('updated_at')) {
                  const date = new Date(parseInt(value));
                  displayValue = (
                    <div className="space-y-0.5">
                      <div className="text-gray-700 font-medium">
                        {date.toLocaleString('en-IN', { 
                          dateStyle: 'medium', 
                          timeStyle: 'short' 
                        })}
                      </div>
                      <div className="text-gray-400 text-[9px] font-mono">{value}</div>
                    </div>
                  );
                } else if (key.includes('status')) {
                  const statusColors = {
                    'FINISHED': 'bg-indigo-100 text-indigo-700',
                    'ACTIVE': 'bg-green-100 text-green-700',
                    'PENDING': 'bg-yellow-100 text-yellow-700',
                    'FAILED': 'bg-red-100 text-red-700',
                    'UNREGISTERED': 'bg-gray-100 text-gray-700'
                  };
                  const colorClass = statusColors[value] || 'bg-gray-100 text-gray-700';
                  displayValue = (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colorClass}`}>
                      {value}
                    </span>
                  );
                } else if (key.includes('id')) {
                  displayValue = (
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-[10px] font-mono text-gray-800 break-all">
                      {value}
                    </code>
                  );
                } else if (key.includes('mobile_number')) {
                  displayValue = (
                    <span className="font-semibold text-gray-800">
                      📱 {value}
                    </span>
                  );
                } else if (key === 'name') {
                  displayValue = (
                    <span className="font-semibold text-gray-800">
                      👤 {value}
                    </span>
                  );
                } else {
                  displayValue = <span className="text-gray-700">{String(value)}</span>;
                }

                const formattedKey = key
                  .split('_')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');

                return (
                  <tr key={key} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 font-semibold text-gray-600 align-top w-1/3">
                      {formattedKey}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {displayValue}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    )}
  </div>
)}


                </div>
              ) : (
                <div className="bg-gray-100 rounded-xl shadow p-4 border border-gray-300 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading Super Key...</p>
                  </div>
                </div>
              )}

              {/* RIGHT: Device Key Management - ALWAYS SHOWS WHEN keyId EXISTS */}
              <div className="bg-white rounded-xl shadow-lg p-2 border border-green-300">
                <h4 className="text-md font-bold mb-2 flex items-center gap-2 text-green-800">
                  🔐 Device Key
                </h4>
                
                <div className="bg-green-50 p-1 rounded-lg mb-2 break-all border-2 border-green-300">
                  <p className="text-xs font-mono text-green-900">{keyId}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button 
                    onClick={refreshKeyInternal} 
                    disabled={isLoading}
                    className="bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 text-xs font-medium transition-all flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    <FaSync size={12}  /> Refresh
                  </button>
                 
                 <button 
                  onClick={removeKeyInternal}
                    disabled={isLoading}
                    className="bg-red-600 text-white py-2 px-3 rounded-lg hover:bg-red-700 text-xs font-medium transition-all flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    🗑️ remove
                  </button> 

               
                  <button 
                    onClick={unregisterKeyInternal} 
                    disabled={isLoading}
                    className="bg-purple-600 text-white py-2 px-3 rounded-lg hover:bg-purple-700 text-xs font-medium transition-all disabled:opacity-50"
                  >
                    🔓 Unregister
                  </button>
                </div>

               {actionResult?.type === "deviceKey" && (
  <div className={`p-3 rounded-lg text-xs border-2 ${
    actionResult.status === "success" 
      ? "bg-green-50 border-green-300" 
      : "bg-red-50 border-red-300"
  }`}>
    <p className={`font-bold mb-2 ${
      actionResult.status === "success" ? "text-green-800" : "text-red-800"
    }`}>
      {actionResult.message}
    </p>
    {actionResult.data && (
      <details className="mt-2">
        <summary className="cursor-pointer text-blue-700 font-semibold hover:text-blue-900">
          📄 View Response Details
        </summary>
        <div className="mt-2 bg-white rounded-lg border-2 border-gray-200 overflow-hidden max-h-80 overflow-y-auto">
          <table className="min-w-full text-[11px]">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-gray-700 border-b">Field</th>
                <th className="px-3 py-2 text-left font-bold text-gray-700 border-b">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(actionResult.data).map(([key, value]) => {
                // Format the value based on type
                let displayValue;
                
                if (value === null || value === undefined) {
                  displayValue = <span className="text-gray-400 italic">null</span>;
                } else if (typeof value === 'boolean') {
                  displayValue = (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {value ? '✓ True' : '✗ False'}
                    </span>
                  );
                } else if (Array.isArray(value)) {
                  // Special handling for journey array
                  if (key === 'journey' && value.length > 0) {
                    displayValue = (
                      <div className="space-y-1">
                        {value.map((item, idx) => (
                          <div key={idx} className="bg-blue-50 rounded px-2 py-1 text-[10px] border border-blue-200">
                            <span className="font-semibold text-blue-700">{item.state}</span>
                            <span className="text-gray-500 ml-2">
                              {new Date(item.created_at).toLocaleString('en-IN', { 
                                dateStyle: 'short', 
                                timeStyle: 'short' 
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  } else if (key === 'frps' && value.length > 0) {
                    // Special handling for FRP accounts
                    displayValue = (
                      <div className="space-y-1">
                        {value.map((frp, idx) => (
                          <div key={idx} className="bg-purple-50 rounded px-2 py-1 text-[10px] border border-purple-200">
                            <div className="font-semibold text-purple-700">📧 {frp.email_id}</div>
                            <div className="text-gray-600 font-mono text-[9px]">Code: {frp.email_code}</div>
                          </div>
                        ))}
                      </div>
                    );
                  } else if (value.length === 0) {
                    displayValue = <span className="text-gray-400 italic">Empty Array []</span>;
                  } else {
                    displayValue = (
                      <details className="inline">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-[10px]">
                          Array ({value.length} items) ▼
                        </summary>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-[9px] overflow-auto max-h-24 border">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </details>
                    );
                  }
                } else if (typeof value === 'object') {
                  // Special handling for device_info
                  if (key === 'device_info') {
                    displayValue = (
                      <div className="bg-gray-50 rounded px-2 py-1 space-y-0.5">
                        <div className="text-[10px]"><span className="font-semibold">IMEI 1:</span> {value.imei_no}</div>
                        <div className="text-[10px]"><span className="font-semibold">IMEI 2:</span> {value.imei_no2}</div>
                        <div className="text-[10px]"><span className="font-semibold">Device:</span> {value.manufacturer} {value.model}</div>
                      </div>
                    );
                  } else {
                    displayValue = (
                      <details className="inline">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-[10px]">
                          Object ▼
                        </summary>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-[9px] overflow-auto max-h-24 border">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </details>
                    );
                  }
                } else if (key.includes('created_at') || key.includes('updated_at')) {
                  // Format timestamps
                  const date = new Date(parseInt(value));
                  displayValue = (
                    <div className="space-y-0.5">
                      <div className="text-gray-700 font-medium">
                        {date.toLocaleString('en-IN', { 
                          dateStyle: 'medium', 
                          timeStyle: 'short' 
                        })}
                      </div>
                      <div className="text-gray-400 text-[9px] font-mono">{value}</div>
                    </div>
                  );
                } else if (key.includes('status')) {
                  // Highlight status fields
                  const statusColors = {
                    'ENROLLED': 'bg-green-100 text-green-700',
                    'FINISHED': 'bg-indigo-100 text-indigo-700',
                    'ACTIVE': 'bg-green-100 text-green-700',
                    'PENDING': 'bg-yellow-100 text-yellow-700',
                    'FAILED': 'bg-red-100 text-red-700',
                    'UNREGISTERED': 'bg-gray-100 text-gray-700',
                    'REGISTERED': 'bg-blue-100 text-blue-700',
                    'CREATED': 'bg-blue-100 text-blue-700'
                  };
                  const colorClass = statusColors[value] || 'bg-gray-100 text-gray-700';
                  displayValue = (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colorClass}`}>
                      {value}
                    </span>
                  );
                } else if (key.includes('id') || key.includes('reference')) {
                  // Format IDs with monospace font
                  displayValue = (
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-[10px] font-mono text-gray-800 break-all">
                      {value}
                    </code>
                  );
                } else if (key === 'version') {
                  displayValue = (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">
                      {value}
                    </span>
                  );
                } else if (key === 'gateway') {
                  displayValue = (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                      {value}
                    </span>
                  );
                } else if (key === 'enrolled_device_imei') {
                  displayValue = (
                    <span className="font-mono text-gray-700 bg-yellow-50 px-2 py-0.5 rounded">
                      📱 {value}
                    </span>
                  );
                } else if (typeof value === 'number') {
                  displayValue = (
                    <span className="font-mono text-gray-700">
                      {value.toLocaleString('en-IN')}
                    </span>
                  );
                } else {
                  displayValue = <span className="text-gray-700">{String(value)}</span>;
                }

                // Format key name (convert snake_case to Title Case)
                const formattedKey = key
                  .split('_')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');

                return (
                  <tr key={key} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 font-semibold text-gray-600 align-top w-1/3">
                      {formattedKey}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {displayValue}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    )}
  </div>
               )}

              </div>
            </div>
          </div>
        )}

       
       {/* Device Controls Section */}
{keyId && (
  <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-1 md:p-2 rounded-xl border-2 border-gray-300">
    <h4 className="font-bold text-md mb-3 flex items-center gap-2 text-gray-800">
      <FaMobileAlt className="text-blue-600" /> Device Controls
      {keyVersion && keyVersion.length > 0 && (
        <span className="text-xs text-gray-600 font-normal">
          (Version: {keyVersion.join(', ')})
        </span>
      )}
    </h4>

    {controlList.length === 0 ? (
      <div className="text-center py-8 bg-white rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Loading controls...</p>
      </div>
    ) : (
      <>
        {/* Control Selection Grid */}
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-1 mb-2">
          {getFilteredControls().length > 0 ? (
            getFilteredControls().map((control) => (
              <button
                key={control.id}
                onClick={() => handleControlSelect(control)}
                disabled={isLoading}
                className={`p-1 rounded-xl border-2 text-xs transition-all flex flex-col items-center gap-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedControl?.id === control.id
                    ? 'border-blue-500 bg-blue-100 shadow-lg scale-105'
                    : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-md'
                }`}
              >
                {control.ui_config_item?.icon && (
                  <img 
                    src={control.ui_config_item.icon} 
                    alt="" 
                    className="w-8 h-8 object-contain" 
                  />
                )}
                <span className="font-medium text-[10px] text-center leading-tight">
                  {control.ui_config_item?.header || control.name}
                </span>
              </button>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500 py-8 bg-white rounded-lg">
              <p className="text-sm">No controls available for this device version</p>
              <p className="text-xs text-gray-400 mt-1">
                Version filter: {keyVersion?.join(', ') || 'None'}
              </p>
            </div>
          )}
        </div>


{/* Dynamic Action Buttons Section */}
{selectedControl && (() => {
  const validActions = (controlActions || []).filter(
    (item) => typeof item.action === "string" && item.action.trim().length > 0
  );

  return (
    <div className="bg-white p-2 rounded-xl border-2 border-blue-300 shadow-md">
      <p className="text-sm mb-2 font-bold text-blue-900">
        Selected: {selectedControl.ui_config_item?.header || selectedControl.name}
      </p>

      {validActions.length === 0 ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-xs text-gray-600">Loading actions...</p>
        </div>
      ) : (
        <div
          className={
            validActions.length <= 3
              ? "flex flex-wrap justify-start gap-3"
              : "grid gap-3"
          }
          style={
            validActions.length > 3
              ? { gridTemplateColumns: `repeat(${Math.min(validActions.length, 6)}, minmax(0, 1fr))` }
              : {}
          }
        >
          {validActions.map((actionItem, index) => {

            // 🎨 Button color
            const getButtonStyle = () => {
              const actionUpper = actionItem.action.toUpperCase();

              if (actionUpper === "ON" || actionUpper === "ENABLE") return "bg-green-600 hover:bg-green-700";
              if (actionUpper === "OFF" || actionUpper === "DISABLE") return "bg-red-600 hover:bg-red-700";
              if (actionUpper === "SEND" || actionUpper === "FETCH") return "bg-blue-600 hover:bg-blue-700";
              if (actionUpper === "UNREGISTER" || actionUpper === "DELETE") return "bg-orange-600 hover:bg-orange-700";

              return "bg-indigo-600 hover:bg-indigo-700";
            };

            // 🧩 Icons
            const getIcon = () => {
              const actionUpper = actionItem.action.toUpperCase();

              if (actionUpper === "ON" || actionUpper === "ENABLE") return "▶️";
              if (actionUpper === "OFF" || actionUpper === "DISABLE") return "⏹️";
              if (actionUpper === "SEND" || actionUpper === "FETCH") return "📤";
              if (actionUpper === "UNREGISTER") return "🔓";
              if (actionUpper === "DELETE") return "🗑️";

              return "⚡";
            };

            return (
              <button
                key={index}
                onClick={() => handleApplyAction(actionItem.action)}
                disabled={isLoading}
                title={actionItem.toast || actionItem.text}
                className={`text-white py-2 px-2 rounded-lg text-sm font-semibold transition-all
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2
                ${getButtonStyle()} min-w-[140px] max-w-[200px]`}
              >
                <span>{getIcon()}</span>
                <span className="truncate">
                  {actionItem.name || actionItem.text || actionItem.action}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
})()}



        {/* Action Info Buttons */}
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="border-2 border-indigo-200 rounded-xl p-2 bg-white">
            <button
              onClick={handleGetAction}
              disabled={isLoading}
              className="w-full bg-indigo-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-indigo-700 mb-3 font-semibold transition-all disabled:opacity-50"
            >
              📋 Get Actions
            </button>
            {Array.isArray(actionResult?.data) && actionResult?.type === "info" && (
              <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-auto">
                {actionResult?.data?.map((item) => {
                  const location = item.response?.location;
                  const mapUrl = location ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}` : null;

                  return (
                    <div key={item.id} className="flex items-center justify-between bg-gray-50 border rounded-lg px-2 py-2 text-xs hover:shadow-md transition-all">
                      <span className="font-medium text-gray-700 truncate max-w-[120px]">{item.action}</span>
                      <div className="flex items-center gap-2">
                        {location && (
                          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-lg" title="Open in Google Maps">
                            📍
                          </a>
                        )}
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                          item.status === "COMPLETED" ? "bg-green-100 text-green-700" :
                          item.status === "FAILED" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-2 border-teal-200 rounded-xl p-2 bg-white">
            <button
              onClick={handleGetStatus}
              disabled={isLoading}
              className="w-full bg-teal-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-teal-700 mb-3 font-semibold transition-all disabled:opacity-50"
            >
              ✓ Check Status 
            </button>
            {actionResult?.data?.items && actionResult?.type === "info" && (
              <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-auto">
                {actionResult?.data?.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center text-xs bg-gray-50 border rounded-lg px-1 py-1  md:px-2 md:py-2 hover:shadow-md transition-all">
                    <span className="font-medium text-gray-700 truncate">{item.name}</span>
                    {item.action_status ? (
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                        item.action_status === "on" ? "bg-green-100 text-green-700" :
                        item.action_status === "off" ? "bg-gray-200 text-gray-700" :
                        item.action_status === "unlocked" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {item.action_status.toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-[10px]">N/A</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    )}
  </div>
)}


      </div>
      
      <div className="sticky bottom-0 bg-gradient-to-r from-gray-100 to-gray-200 px-2 py-1 border-t-2 flex justify-end gap-2 rounded-b-lg">
        <button 
          onClick={() => {
            setShowModal(false);
            setActionResult(null);
            setSelectedControl(null);
          }} 
          className="px-2 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold transition-all"
        >
          ✕ Close
        </button>
      </div>
    </div>
  </div>
)}
{/* Confirmation Modal */}
<ConfirmationModal
  isOpen={confirmModal.isOpen}
  title={confirmModal.title}
  message={confirmModal.message}
  confirmText={confirmModal.confirmText}
  cancelText={confirmModal.cancelText}
  type={confirmModal.type}
  onConfirm={confirmModal.onConfirm}
  onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
/>

  </>
);

};

export default MobileDeviceLock;



// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText, cancelText, type }) => {
  if (!isOpen) return null;

  const typeStyles = {
    danger: {
      bg: "bg-red-50",
      border: "border-red-300",
      icon: "🚨",
      confirmBtn: "bg-red-600 hover:bg-red-700",
      titleColor: "text-red-800"
    },
    warning: {
      bg: "bg-yellow-50",
      border: "border-yellow-300",
      icon: "⚠️",
      confirmBtn: "bg-yellow-600 hover:bg-yellow-700",
      titleColor: "text-yellow-800"
    },
    info: {
      bg: "bg-blue-50",
      border: "border-blue-300",
      icon: "ℹ️",
      confirmBtn: "bg-blue-600 hover:bg-blue-700",
      titleColor: "text-blue-800"
    }
  };

  const style = typeStyles[type] || typeStyles.info;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-2">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className={`${style.bg} border-b-2 ${style.border} px-6 py-4 rounded-t-xl`}>
          <h3 className={`text-lg font-bold ${style.titleColor} flex items-center gap-2`}>
            <span className="text-2xl">{style.icon}</span>
            {title}
          </h3>
        </div>
        
        <div className="px-2 py-3">
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
            {message}
          </p>
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-5 py-2 text-sm font-bold text-white rounded-lg transition-all shadow-md ${style.confirmBtn}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
